/**
 * Pure wording for customer complaint pills and the Complaints tab
 * (kanban 2a28d4c0). Components: ComplaintPill.tsx, CustomerComplaintsTab.tsx.
 */
import type {
  CustomerComplaint,
  CustomerQualitySummary,
} from '../../../services/portalQualityAsks';
import { formatShortDate } from '../../../utils/dateFormatters';

/** "⚑ 2 open complaints · INV-00004, INV-00007"; null when none are open. */
export function openPillLabel(summary: CustomerQualitySummary): string | null {
  if (summary.open_count === 0) return null;
  const noun = summary.open_count === 1 ? 'open complaint' : 'open complaints';
  return `⚑ ${summary.open_count} ${noun} · ${summary.open.map((o) => o.ref).join(', ')}`;
}

/** "waiting on a colleague · last update Oct 8": who the first open item
 *  waits on, and the newest update across ALL open items (a complaint filed
 *  a minute ago reads "today", not the older item's date). */
export function openPillDetail(summary: CustomerQualitySummary, now: Date = new Date()): string | null {
  const first = summary.open[0];
  if (!first) return null;
  const parts: string[] = [];
  if (first.waits_on) parts.push(`waiting on ${first.waits_on}`);
  parts.push(`last update ${dayWord(summary.last_update_at ?? newestUpdate(summary), now)}`);
  return parts.join(' · ');
}

/** "✓ complaint INV-00002 completed Sep 11"; only when nothing is open. */
export function completedPillLabel(summary: CustomerQualitySummary): string | null {
  if (summary.open_count > 0 || !summary.recently_completed) return null;
  const done = summary.recently_completed;
  return `✓ complaint ${done.ref} completed ${formatShortDate(done.completed_at)}`;
}

function newestUpdate(summary: CustomerQualitySummary): string {
  return summary.open.reduce(
    (best, o) => (new Date(o.updated_at).getTime() > new Date(best).getTime() ? o.updated_at : best),
    summary.open[0].updated_at,
  );
}

function dayWord(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (d.toDateString() === now.toDateString()) return 'today';
  return formatShortDate(iso);
}

const STATUS_WORD: Record<string, string> = {
  investigating: 'Investigating',
  remediating: 'Remediating',
  completed: 'Completed',
  cancelled: 'Cancelled',
  open: 'New, in triage',
  routed: 'With a person',
  in_investigation: 'Became a case',
  dismissed: 'Dismissed',
};

export function complaintStatusLabel(item: CustomerComplaint): string {
  return STATUS_WORD[item.status] ?? item.status;
}

/** "Waiting on a colleague", "Completed Sep 11", or "Closed". */
export function complaintWaitLabel(item: CustomerComplaint): string {
  if (item.is_open) return item.waits_on ? `Waiting on ${item.waits_on}` : 'Open';
  if (item.completed_at) return `Closed ${formatShortDate(item.completed_at)}`;
  return 'Closed';
}

const DOOR = '/portal/quality-copilot';

/** Where a Complaints-tab row opens on the quality door: its case (a thread
 *  that became a case opens the case), else the thread. Only for viewers
 *  who can open the door; the sales-site complaint has no portal page. */
export function complaintDoorPath(item: CustomerComplaint): string {
  if (item.kind === 'case') return `${DOOR}/cases/${encodeURIComponent(item.ref)}`;
  if (item.case_ref) return `${DOOR}/cases/${encodeURIComponent(item.case_ref)}`;
  return `${DOOR}/threads/${encodeURIComponent(item.ref)}`;
}

/** Index summaries by upper-cased customer number for table lookups. */
export function summariesByCustomer(
  summaries: CustomerQualitySummary[],
): Map<string, CustomerQualitySummary> {
  return new Map(summaries.map((s) => [s.customer_no.toUpperCase(), s]));
}
